import { useAdminTenantsApi } from "hooks/useAdminApi";
import { getActiveTenantId, setActiveTenantId } from "lib/active-tenant";
import type { FC } from "react";

type TenantSwitcherProps = {
  /** サイドバーが展開中か（折りたたみ時は表示しない） */
  isSidebarOpen?: boolean;
};

/**
 * システム管理者向けのテナント切替セレクタ。
 *
 * 選択すると X-Act-As-Tenant-Id をローカルに保存し、トップへ遷移して全データを
 * 切替先テナントで再取得する。表示・取得ともに管理者のみ（呼び出し側で
 * canAccessAdminPages により描画をガードし、テナント一覧APIも管理者限定）。
 * @param {object} props プロパティ
 * @param {boolean} [props.isSidebarOpen] サイドバー展開状態
 * @return {JSX.Element | null} 切替UI（展開時のみ。折りたたみ時は null）
 */
export const TenantSwitcher: FC<TenantSwitcherProps> = ({ isSidebarOpen }) => {
  const { data, loading } = useAdminTenantsApi();
  const active = getActiveTenantId();

  if (!isSidebarOpen) return null;

  const tenants = data?.items ?? [];

  /**
   * テナント選択変更時の処理（保存してトップへ再読込）。
   * @param {string} value 選択されたテナントID（空文字で自テナント）
   * @return {void}
   */
  const handleChange = (value: string): void => {
    setActiveTenantId(value || null);
    window.location.assign("/");
  };

  return (
    <div className="px-2 mb-3" data-testid="tenant-switcher">
      <div className="text-[10px] uppercase tracking-wide text-amber-300/80 mb-1">
        テナント切替 (管理者)
      </div>
      <select
        className="w-full text-xs rounded-md bg-white/10 text-white border border-white/20 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-300"
        value={active ?? ""}
        disabled={loading}
        onChange={e => handleChange(e.target.value)}
        aria-label="テナント切替"
      >
        <option value="">（自テナント）</option>
        {tenants.map(t => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
      {active && (
        <div className="mt-1 text-[10px] text-amber-300">
          ⚠ 別テナントを閲覧中
        </div>
      )}
    </div>
  );
};
